<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { FormEditorSaveEventPayload } from '@Alternatives/Forms/Components/FormEditor'
    import type { FormEditorViewModel } from '@Alternatives/Forms/Components/FormEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: FormEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'save-form', payload: FormEditorSaveEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const title = computed(() => vm.value.title)
    const confirmButtonLabel = computed(() => vm.value.confirmButtonLabel)
    const nameError = computed(() => vm.value.nameError)

    // writable computed (replaces getter + setter)
    const name = computed<string | null>({
        get: () => vm.value.name,
        set: val => { vm.value.name = val }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        vm.value.clearValidationErrors()

        emit(
            'save-form',
            new FormEditorSaveEventPayload(
                vm.value.id,
                name.value,
                vm.value.sourceId
            )
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{title}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="form-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input v-model.trim="name"
                                       type="text"
                                       class="form-control"
                                       required
                                       maxlength="100"
                                       minlength="2">
                                <div class="error">{{nameError}}</div>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer">
                        <button type="button"
                                @click="close"
                                class="btn btn-outline-secondary">
                            Cancel
                        </button>
                        <button type="submit"
                                form="form-editor-form"
                                class="btn btn-primary">
                            {{confirmButtonLabel}}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
